import { useEffect, useState } from 'react';
import { invitationAPI, type InvitationPasswordCheck } from '../lib/api';
import { useDebouncedValue } from './useDebouncedValue';

/** Quiet period after the last keystroke before the server is asked. */
const CHECK_DELAY_MS = 400;

export interface InvitationPasswordCheckState {
  /** The server's answer for exactly the current password, once it has one. */
  result: InvitationPasswordCheck | null;
  /** A check for the current password is waiting on the debounce or in flight. */
  checking: boolean;
  /**
   * The check could not be made (offline, throttled). The server still
   * enforces the full policy on submit, so the page may fall back to the
   * rules it can judge itself rather than block the invitee.
   */
  unavailable: boolean;
}

interface Answer {
  password: string;
  result: InvitationPasswordCheck | null;
}

/**
 * CR-8598: ask the server which password rules the invitee's password meets.
 *
 * Only the server can say whether a password has been in a data breach, and
 * the Accept button must stay disabled until every rule passes. Calls are
 * debounced, and an answer only counts for the password it was asked about,
 * so a slow reply for an earlier keystroke can never enable the button.
 * (Not `useFetchData`: that hook has no notion of which input a result
 * belongs to, which is exactly what keeps this correct.)
 *
 * @param token - The invitation token from the link
 * @param password - The password as currently typed
 * @param enabled - Ask only once the rules the browser can judge already pass
 * @returns The server's answer for the current password, and whether one is pending
 * @example
 * const check = useInvitationPasswordCheck(token, password, localRulesPass);
 * const ready = check.result?.valid ?? false;
 */
export function useInvitationPasswordCheck(
  token: string | null,
  password: string,
  enabled: boolean,
): InvitationPasswordCheckState {
  const debounced = useDebouncedValue(password, CHECK_DELAY_MS);
  const [answer, setAnswer] = useState<Answer | null>(null);

  useEffect(() => {
    // Still typing (debounce not caught up), or not worth asking yet.
    if (!token || !enabled || debounced !== password) return undefined;
    let cancelled = false;
    invitationAPI.checkPassword({ token, password: debounced })
      .then((res) => { if (!cancelled) setAnswer({ password: debounced, result: res.data }); })
      .catch(() => { if (!cancelled) setAnswer({ password: debounced, result: null }); });
    return () => { cancelled = true; };
  }, [token, enabled, debounced, password]);

  const current = enabled && answer?.password === password ? answer : null;
  return {
    result: current?.result ?? null,
    checking: enabled && current === null,
    unavailable: current !== null && current.result === null,
  };
}
